// Invoice drafts: every invoice folder built on Billing › Invoice Workbook is
// recorded in Fusion, so someone other than the builder (Nathan) can see
// what was built, check its numbers and mark it validated, or send it back,
// before it goes to the client (Steve 11/3/26: "track drafts in fusion so
// other people can validate it").
//
// A draft is one Billing user-data row, like the invoice exceptions
// (billingInvoiceExceptions.js): fieldName 'invoice', rowId
// 'invoice_draft_<id>', Notes = the draft as JSON. It holds the weeks, who
// built it and when, the zip's name, the totals the model priced (whole and
// by week), the switches it was built with, how the checks came out, and the
// reviews: { by, at, status, note }, newest last. A blank Notes deletes it.
//
// The draft's status is derived, never set by hand: Sent once every week of
// it is on a labor or equipment invoice approval in DocuSign (invoicedWeeks),
// else the last review's word (Validated / Needs changes), else Draft.

import React, { useState } from 'react';
import moment from 'moment';
import { INVOICE_FIELD } from './billingInvoiceExceptions';

export const DRAFT_PREFIX = 'invoice_draft_';
export const DRAFT_STATUS = { draft: 'Draft', validated: 'Validated', changes: 'Needs changes', sent: 'Sent' };
const REVIEW_CAP = 8;

const str = (v) => (v == null ? '' : String(v).trim());
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const r2 = (n) => Math.round(num(n) * 100) / 100;
const newId = () => `${DRAFT_PREFIX}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** The draft of a folder build: the model's totals, the checks' outcome, the switches. */
export const newDraft = ({ weeks, model, checks = {}, built = null, options = {}, by = '', at = null }) => {
    const ws = [...new Set(weeks || [])].sort();
    const t = model?.totals || {};
    return {
        id: newId(),
        weeks: ws,
        file: str(built?.name),
        by: str(by).slice(0, 40),
        at: at || moment().format('YYYY-MM-DD HH:mm'),
        totals: { hrs: r2(t.hrs), labor: r2(t.labor), pd: r2(t.pd), equip: r2(t.equip), fuel: r2(t.fuel), total: r2(t.total) },
        byWeek: (model?.byWeek || []).map((w) => ({
            we: w.we, hrs: r2(w.hrs), labor: r2(w.labor), pd: r2(w.pd), equip: r2(w.equip), fuel: r2(w.fuel), total: r2(w.total),
            people: num(w.people), units: num(w.units), lb: str(w.laborBasis), eb: str(w.equipBasis),
        })),
        checks: {
            blocks: (checks.blocks || []).length,
            warnings: (checks.warnings || []).length,
            accepted: (checks.accepted || []).length,
            settled: (checks.settled || []).length,
        },
        options: { acceptMaster: Boolean(options.acceptMaster), allowUnsigned: Boolean(options.allowUnsigned) },
        reviews: [],
    };
};

/** The draft with a review on it: status 'validated' | 'changes', with who, when and the note. */
export const reviewDraft = (draft, { by, status, note = '', at = null }) => ({
    ...draft,
    reviews: [...(draft.reviews || []), { by: str(by).slice(0, 40), at: at || moment().format('YYYY-MM-DD HH:mm'), status, note: str(note).slice(0, 400) }].slice(-REVIEW_CAP),
});

/** The last review on a draft, or null. */
export const lastReview = (draft) => (draft.reviews && draft.reviews.length ? draft.reviews[draft.reviews.length - 1] : null);

/**
 * Where a draft stands: Sent when every one of its weeks is on an invoice
 * approval in DocuSign (`invoiced` is invoicedWeeks' map), else the last
 * review's word, else Draft.
 */
export const draftStatus = (draft, invoiced = {}) => {
    if (draft.weeks.length && draft.weeks.every((we) => invoiced[we]?.length)) return DRAFT_STATUS.sent;
    const r = lastReview(draft);
    if (r?.status === 'validated') return DRAFT_STATUS.validated;
    if (r?.status === 'changes') return DRAFT_STATUS.changes;
    return DRAFT_STATUS.draft;
};

// ── rows (billing.js saveRows: { fieldName, rowId, notes }) ──
export const draftRow = (draft) => ({ fieldName: INVOICE_FIELD, rowId: draft.id, notes: JSON.stringify({ ...draft, id: undefined }) });
export const deleteDraftRow = (draft) => ({ fieldName: INVOICE_FIELD, rowId: draft.id, notes: '' });

/** The job's drafts off its Billing rows, newest build first. */
export const parseInvoiceDrafts = (userRows) => {
    const out = [];
    (userRows || []).forEach((x) => {
        if (String(x?.Forecast_Field_Name || '') !== INVOICE_FIELD) return;
        const id = String(x.Forecast_Row_ID || '');
        if (!id.startsWith(DRAFT_PREFIX) || !str(x.Notes)) return;
        let p;
        try { p = JSON.parse(x.Notes); } catch { return; }
        if (!p || !Array.isArray(p.weeks) || !p.weeks.length) return;
        out.push({
            id,
            weeks: p.weeks.map(String).sort(),
            file: str(p.file),
            by: str(p.by),
            at: str(p.at),
            totals: { hrs: r2(p.totals?.hrs), labor: r2(p.totals?.labor), pd: r2(p.totals?.pd), equip: r2(p.totals?.equip), fuel: r2(p.totals?.fuel), total: r2(p.totals?.total) },
            byWeek: Array.isArray(p.byWeek) ? p.byWeek : [],
            checks: { blocks: num(p.checks?.blocks), warnings: num(p.checks?.warnings), accepted: num(p.checks?.accepted), settled: num(p.checks?.settled) },
            options: { acceptMaster: Boolean(p.options?.acceptMaster), allowUnsigned: Boolean(p.options?.allowUnsigned) },
            reviews: Array.isArray(p.reviews) ? p.reviews.filter((r) => r && (r.status === 'validated' || r.status === 'changes')) : [],
        });
    });
    return out.sort((a, b) => b.at.localeCompare(a.at) || b.id.localeCompare(a.id));
};

/** The drafts whose weeks overlap these, newest first. */
export const draftsOver = (drafts, weeks) => {
    const set = new Set(weeks || []);
    return (drafts || []).filter((d) => d.weeks.some((we) => set.has(we)));
};

// ── the card ──
const money = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const hours = (n) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
const weekRange = (weeks) => {
    if (!weeks.length) return '';
    const from = moment(weeks[0], 'YYYY-MM-DD').subtract(6, 'days');
    const to = moment(weeks[weeks.length - 1], 'YYYY-MM-DD');
    return `${from.format('M/D')}–${to.format('M/D/YY')}`;
};
const fmtAt = (at) => (at ? moment(at, 'YYYY-MM-DD HH:mm').format('M/D/YY h:mm a') : '');

const TONE = {
    [DRAFT_STATUS.draft]: { bg: 'var(--muted)', fg: 'var(--text-secondary)' },
    [DRAFT_STATUS.validated]: { bg: 'var(--success-tint)', fg: 'var(--success-text)' },
    [DRAFT_STATUS.changes]: { bg: 'var(--warning-tint)', fg: 'var(--warning-text)' },
    [DRAFT_STATUS.sent]: { bg: 'var(--primary-tint)', fg: 'var(--primary)' },
};
const Pill = ({ status }) => {
    const t = TONE[status] || TONE[DRAFT_STATUS.draft];
    return <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 'var(--radius-pill)', fontSize: 12, fontWeight: 500, background: t.bg, color: t.fg, whiteSpace: 'nowrap' }}>{status}</span>;
};
const btn = (kind, disabled) => ({
    height: 28, padding: '0 10px', borderRadius: 'var(--radius)', fontFamily: 'inherit', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
    ...(kind === 'primary'
        ? { border: '1px solid transparent', background: 'var(--primary)', color: 'var(--primary-foreground)' }
        : kind === 'link'
            ? { border: '1px solid transparent', background: 'transparent', color: 'var(--primary)', padding: '0 6px' }
            : { border: '1px solid var(--border)', background: 'var(--background)', color: 'var(--foreground)', boxShadow: 'var(--shadow-xs)' }),
});
const Btn = ({ label, onClick, kind = 'outline', disabled, title }) => (
    <button type="button" onClick={onClick} disabled={disabled} title={title} style={btn(kind, disabled)}>{label}</button>
);
const th = { fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)', textAlign: 'left', background: 'var(--subtle)', borderBottom: '1px solid var(--border)', padding: '8px 12px', whiteSpace: 'nowrap' };
const td = { fontSize: 13, padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--foreground)', verticalAlign: 'top' };
const num_ = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

/**
 * The job's invoice drafts: what was built, by whom, its totals, and where
 * it stands. Validate / Needs changes write a review with a note; Rebuild
 * picks the draft's weeks and builds again; Remove deletes it.
 *   drafts    parseInvoiceDrafts(userRows)
 *   invoiced  invoicedWeeks(envelopes, weeks) — Sent is read off it
 *   onReview  (draft, { status, note }) => void
 *   onRebuild (draft) => void
 *   onRemove  (draft) => void
 */
export const InvoiceDrafts = ({ drafts, invoiced = {}, userName = '', onReview, onRebuild, onRemove, disabled = false, canEdit = true, highlight = null }) => {
    // The draft just built (Labor & Equipment's Create invoice lands here on it): lit, and in view.
    const litRef = React.useRef(null);
    React.useEffect(() => { if (highlight && litRef.current?.scrollIntoView) litRef.current.scrollIntoView({ block: 'center' }); }, [highlight]);
    const [open, setOpen] = useState(null); // { id, status, note } — the review being written
    const [showAll, setShowAll] = useState(false);
    const list = showAll ? drafts : drafts.slice(0, 6);
    const submit = () => {
        if (!open) return;
        const d = drafts.find((x) => x.id === open.id);
        if (d && onReview) onReview(d, { status: open.status, note: open.note });
        setOpen(null);
    };
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--foreground)' }}>Invoice drafts</span>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {drafts.length
                        ? 'Every invoice folder built here, so someone else can check it before it goes out. A draft reads Sent once its weeks are on an invoice approval in DocuSign.'
                        : 'None yet. Build invoice folder records a draft here for someone else to validate.'}
                </span>
            </div>
            {drafts.length ? (
                <div style={{ overflowX: 'auto', marginTop: 10 }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={th}>Period</th>
                                <th style={th}>Built</th>
                                <th style={{ ...th, ...num_ }}>Hours</th>
                                <th style={{ ...th, ...num_ }}>Labor</th>
                                <th style={{ ...th, ...num_ }}>Per diem</th>
                                <th style={{ ...th, ...num_ }}>Equipment</th>
                                <th style={{ ...th, ...num_ }}>Fuel</th>
                                <th style={{ ...th, ...num_ }}>Total</th>
                                <th style={th}>Checks</th>
                                <th style={th}>Status</th>
                                <th style={th}>Review</th>
                                <th style={th} />
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((d) => {
                                const status = draftStatus(d, invoiced);
                                const rv = lastReview(d);
                                const sent = status === DRAFT_STATUS.sent;
                                const inv = sent ? [...new Set(d.weeks.flatMap((we) => (invoiced[we] || []).map((x) => x.no)))].filter(Boolean) : [];
                                const checkText = [
                                    d.checks.warnings ? `${d.checks.warnings} warning${d.checks.warnings === 1 ? '' : 's'}` : '',
                                    d.checks.accepted ? `${d.checks.accepted} on the card class` : '',
                                    d.checks.settled ? `${d.checks.settled} by exception` : '',
                                    d.options.allowUnsigned ? 'summary still out allowed' : '',
                                ].filter(Boolean).join(' · ') || 'clean';
                                const editing = open?.id === d.id;
                                return (
                                    <React.Fragment key={d.id}>
                                        <tr ref={d.id === highlight ? litRef : undefined} style={d.id === highlight ? { background: 'var(--primary-tint, #e8eef5)' } : undefined}>
                                            <td style={td}>
                                                <div style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{weekRange(d.weeks)}</div>
                                                <div style={{ fontSize: 12, color: 'var(--muted-foreground)' }} title={d.file || undefined}>
                                                    {d.weeks.map((we) => `WE ${moment(we, 'YYYY-MM-DD').format('M/D')}`).join(', ')}
                                                </div>
                                            </td>
                                            <td style={td}>
                                                <div style={{ whiteSpace: 'nowrap' }}>{d.by || '—'}</div>
                                                <div style={{ fontSize: 12, color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>{fmtAt(d.at)}</div>
                                            </td>
                                            <td style={{ ...td, ...num_ }}>{hours(d.totals.hrs)}</td>
                                            <td style={{ ...td, ...num_ }}>{money(d.totals.labor)}</td>
                                            <td style={{ ...td, ...num_ }}>{money(d.totals.pd)}</td>
                                            <td style={{ ...td, ...num_ }}>{money(d.totals.equip)}</td>
                                            <td style={{ ...td, ...num_ }}>{money(d.totals.fuel)}</td>
                                            <td style={{ ...td, ...num_, fontWeight: 600 }}>{money(d.totals.total)}</td>
                                            <td style={{ ...td, fontSize: 12, color: 'var(--text-secondary)' }}>{checkText}</td>
                                            <td style={td}>
                                                <Pill status={status} />
                                                {inv.length ? <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>{inv.join(', ')}</div> : null}
                                            </td>
                                            <td style={{ ...td, fontSize: 12, color: 'var(--text-secondary)', maxWidth: 280, whiteSpace: 'normal' }}>
                                                {rv ? (
                                                    <>
                                                        <span style={{ color: 'var(--foreground)' }}>{rv.by || 'Someone'}</span>
                                                        {` ${fmtAt(rv.at)}`}
                                                        {rv.note ? <div>{rv.note}</div> : null}
                                                        {d.reviews.length > 1 ? <div style={{ color: 'var(--muted-foreground)' }}>{`${d.reviews.length} reviews`}</div> : null}
                                                    </>
                                                ) : 'Not checked yet'}
                                            </td>
                                            <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                                    {canEdit && !sent ? <Btn label="Validate" kind="primary" disabled={disabled} onClick={() => setOpen({ id: d.id, status: 'validated', note: '' })} /> : null}
                                                    {canEdit && !sent ? <Btn label="Needs changes" disabled={disabled} onClick={() => setOpen({ id: d.id, status: 'changes', note: '' })} /> : null}
                                                    {onRebuild ? <Btn label="Rebuild" kind="link" disabled={disabled} title="Pick this draft's weeks and build the invoice folder again" onClick={() => onRebuild(d)} /> : null}
                                                    {canEdit && onRemove ? <Btn label="Remove" kind="link" disabled={disabled} onClick={() => onRemove(d)} /> : null}
                                                </div>
                                            </td>
                                        </tr>
                                        {editing ? (
                                            <tr>
                                                <td colSpan={12} style={{ ...td, background: 'var(--subtle)' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                        <span style={{ fontSize: 13, fontWeight: 500 }}>
                                                            {open.status === 'validated' ? `Validated by ${userName || 'you'}` : `Needs changes, per ${userName || 'you'}`}
                                                        </span>
                                                        <input
                                                            type="text"
                                                            aria-label="Review note"
                                                            placeholder={open.status === 'validated' ? 'What was checked (optional)' : 'What needs to change'}
                                                            value={open.note}
                                                            onChange={(e) => setOpen({ ...open, note: e.target.value })}
                                                            onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') setOpen(null); }}
                                                            style={{ flex: 1, minWidth: 240, height: 32, padding: '0 10px', borderRadius: 'var(--radius)', border: '1px solid var(--input)', background: 'var(--background)', fontFamily: 'inherit', fontSize: 13 }}
                                                        />
                                                        <Btn label="Save" kind="primary" onClick={submit} disabled={open.status === 'changes' && !open.note.trim()} title={open.status === 'changes' && !open.note.trim() ? 'Say what needs to change' : ''} />
                                                        <Btn label="Cancel" kind="link" onClick={() => setOpen(null)} />
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : null}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                    {drafts.length > 6 ? (
                        <div style={{ marginTop: 6 }}>
                            <Btn kind="link" label={showAll ? 'Show the latest 6' : `Show all ${drafts.length}`} onClick={() => setShowAll((v) => !v)} />
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default InvoiceDrafts;
